import { Link } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';

export interface CampaignHighlightData {
  /** Label contextual (ej: "📰 Comunicado", "🌍 Causa destacada") */
  label: string;
  /** Título de la causa */
  title: string;
  /** Descripción corta de la situación */
  description: string;
  /** Ubicación geográfica */
  location: string;
  /** Ruta interna de la campaña */
  link: string;
  /** Texto del botón CTA */
  ctaText: string;
  /** Mensaje de contexto sobre la relación con DonaApp */
  supportMessage: string;
}

interface Props {
  campaign: CampaignHighlightData | null;
}

/**
 * Componente modular para mostrar un comunicado o campaña destacada en la landing.
 * - Se desactiva pasando campaign={null}
 * - Fácilmente reemplazable con otra causa cambiando solo los datos
 * - No debe dominar visualmente el hero ni el mensaje central de la plataforma
 */
export function CampaignHighlight({ campaign }: Props) {
  if (!campaign) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            {/* Label */}
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 mb-3">
              {campaign.label}
            </span>

            {/* Título */}
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
              {campaign.title}
            </h3>

            {/* Descripción */}
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              {campaign.description}
            </p>

            {/* Ubicación */}
            <div className="flex items-center space-x-1.5 text-xs text-gray-500 mb-3">
              <MapPin className="w-3.5 h-3.5" />
              <span>{campaign.location}</span>
            </div>

            {/* Mensaje de soporte */}
            <p className="text-xs text-amber-700 font-medium italic">
              {campaign.supportMessage}
            </p>
          </div>

          {/* CTA */}
          <div className="sm:flex-shrink-0">
            <Link
              to={campaign.link}
              className="inline-flex items-center space-x-2 px-5 py-3 bg-amber-600 text-white font-medium text-sm rounded-lg hover:bg-amber-700 transition-colors"
            >
              <span>{campaign.ctaText}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
